import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, ExternalLinkIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { AudioMaster } from '../../components/AudioMaster';
import { StatusTag } from '../../components/StatusTag';
import { VideoStage } from '../../components/VideoStage';
import { clientStatusLabel, isReviewable, metaLabel } from '../../utils/deliverables';
type Panel = 'idle' | 'confirm' | 'approved';
const EASE = [0.23, 1, 0.32, 1] as const;
export function EpisodeReview() {
  const {
    activeDeliverable,
    reviewDeliverables,
    approveDeliverable,
    requestChanges,
    goClient,
    notify,
    canApprove,
    canComment,
    projectComplete
  } = usePortal();
  const [panel, setPanel] = useState<Panel>('idle');
  const [composerOpen, setComposerOpen] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const [timecode, setTimecode] = useState('');
  if (!activeDeliverable) return null;
  const item = activeDeliverable;
  const index = reviewDeliverables.filter(entry => entry.stage === item.stage).findIndex(entry => entry.id === item.id);
  const number = index >= 0 ? String(index + 1).padStart(2, '0') : '';
  const inReview = isReviewable(item.status);
  const settled = item.status === 'Approved' || item.status === 'Delivered' || item.status === 'Ready for Download';
  const changesRequested = item.status === 'Changes Requested';
  const showVideo = item.type === 'Video' || item.type === 'Other' || item.type === 'Folder';
  const showAudio = item.type === 'Audio' || Boolean(item.audioUrl);

  /** Changes stay open for as long as this version is the current cut. */
  const canAddChanges = canComment && !projectComplete && !settled && (inReview || changesRequested);
  const currentChanges = item.changeRequests.filter(request => request.version === item.version);
  function confirmApproval() {
    approveDeliverable(item.id);
    setPanel('approved');
    setComposerOpen(false);
    notify('Portal notified');
  }
  function addChange(event: React.FormEvent) {
    event.preventDefault();
    const body = changeNote.trim();
    if (!body) return;
    requestChanges(item.id, body, timecode);
    setChangeNote('');
    setTimecode('');
    notify('Change added · Portal has been notified');
  }
  return <div data-id="element-494" data-mp="11cqbmf8d2ts4">
      <button type="button" onClick={() => goClient('episodes')} className="label group flex items-center gap-3 text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-495" data-mp="11czttgn5zwn9">
        <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-496" data-mp="1acwmrugl7ho4" />
        All Deliverables
      </button>

      <header className="mt-10 border-b border-rule pb-10" data-id="element-497" data-mp="3c5krk88vqd60">
        <p className="label text-mute" data-id="element-498" data-mp="3h3mdr21lqslp">{metaLabel(item)}</p>
        <h1 className="mt-6 font-display text-4xl leading-[0.95] tracking-tight sm:text-7xl" data-id="element-499" data-mp="3h44dw9rdxqyw">
          {number && `${number} — `}
          {item.name.toUpperCase()}
        </h1>
        <div className="mt-8 flex flex-wrap items-center gap-x-10 gap-y-4" data-id="element-500" data-mp="34sbqjiga4v4i">
          <StatusTag status={item.status} audience="review" data-id="element-501" data-mp="2ztqt1qsewpjr" />
          {item.version && <p className="label m-0 text-mute" data-id="element-502" data-mp="2uv773qdtjdre">Version {item.version}</p>}
          {item.versionDate !== '—' && <p className="label m-0 text-mute" data-id="element-503" data-mp="3sixsqt3lrl6l">{item.versionDate}</p>}
          {item.runtime !== '—' && <p className="label m-0 text-mute" data-id="element-504" data-mp="3njuupsm1if84">Runtime {item.runtime}</p>}
        </div>
      </header>

      <div className="mt-12 grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16" data-id="element-505" data-mp="3ikrpa725e6zm">
        <div className="lg:col-span-8" data-id="element-506" data-mp="3imun3d9jl4ew">
          {showVideo && <>
              <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-rule pb-4" data-id="element-507" data-mp="3eiwpx83s23oh">
                <h2 className="label m-0 text-graphite" data-id="element-508" data-mp="273i85r6hrr1h">Latest Review Version</h2>
                <p className="label-sm m-0 text-mute" data-id="element-509" data-mp="232g7r70u98xx">
                  {item.version ? `${item.version} · ` : ''}Plays inside Portal
                </p>
              </div>
              <div className="mt-6" data-id="element-510" data-mp="3vsh4vkewj3g7">
                <VideoStage driveUrl={item.driveUrl} label={`${item.name} — ${item.version || 'Review copy'}`} runtime={item.runtime} data-id="element-511" data-mp="3vtih3s7nlvzi" />
              </div>
            </>}

          {!showVideo && !showAudio && <div className="border border-rule px-7 py-9" data-id="element-512" data-mp="dmlxk60u1vgj">
              <p className="label m-0 text-graphite" data-id="element-513" data-mp="il5okkua9xfr">Latest Review Version</p>
              <p className="mt-5 font-display text-2xl" data-id="element-514" data-mp="4lec4ojf0p3v">{item.name}</p>
              {settled ? <p className="label mt-6 text-mute" data-id="element-515" data-mp="8mg4qmitu2wv">{clientStatusLabel(item.status)}</p> : <button type="button" onClick={() => notify('Simulated · would open in Google Drive')} className="label group mt-8 flex items-center gap-3 border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-516" data-mp="2patztanc7xpa">
                  Open for Review
                  <ExternalLinkIcon className="h-3.5 w-3.5" strokeWidth={1.25} data-id="element-517" data-mp="2gwpbnykyjgsl" />
                </button>}
            </div>}

          <div className="mt-12" data-id="element-518" data-mp="2qtk90e5hvb5a">
            <AnimatePresence mode="wait" initial={false} data-id="element-519" data-mp="2qtaf4fhbwjub">
              {panel === 'approved' || settled && panel === 'idle' ? <motion.div key="approved" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              ease: EASE
            }} className="border border-ink px-7 py-9 sm:px-10" data-id="element-520" data-mp="1y43jppd68ada">
                  <p className="flex items-center gap-4 font-display text-3xl leading-none sm:text-4xl" data-id="element-521" data-mp="1kc6sron8o6ih">
                    {item.status === 'Approved' ? 'Approved' : item.status}
                    <CheckIcon className="h-6 w-6" strokeWidth={1} data-id="element-522" data-mp="251hiv1cwn036" />
                  </p>
                  <p className="label mt-5 text-mute" data-id="element-523" data-mp="24zoksagcxo6s">
                    {item.status === 'Approved' ? `${item.version ? `${item.version} approved · ` : ''}Portal has been notified.` : 'Your final files are ready to download.'}
                  </p>
                  <button type="button" onClick={() => goClient('files')} className="label group mt-8 flex items-center gap-3 border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-524" data-mp="2iuqnypttkzfw">
                    Go to Files
                    <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-525" data-mp="202ofw2v8urh9" />
                  </button>
                </motion.div> : projectComplete ? <motion.div key="complete" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              ease: EASE
            }} className="border border-ink px-7 py-9 sm:px-10" data-id="element-526" data-mp="zuhqkd60182m">
                  <p className="font-display text-3xl leading-none sm:text-4xl" data-id="element-527" data-mp="l4ah2mxyyze2">Delivered</p>
                  <p className="mt-5 max-w-lg font-display text-lg text-charcoal" data-id="element-528" data-mp="h4ru7lml8e8l">
                    This project is complete. Final files are available to download.
                  </p>
                  <button type="button" onClick={() => goClient('files')} className="label group mt-8 flex items-center gap-3 border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-529" data-mp="74r7dppiffzn">
                    Go to Files
                    <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-530" data-mp="1wla4p59dgq1r" />
                  </button>
                </motion.div> : panel === 'confirm' ? <motion.div key="confirm" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              ease: EASE
            }} className="border border-ink px-7 py-9 sm:px-10" data-id="element-531" data-mp="wkqh2a7ry999">
                  <h2 className="font-display text-2xl leading-tight sm:text-3xl" data-id="element-532" data-mp="rldhqpzpv9ps">
                    Approve {item.version ? `version ${item.version}` : item.name}?
                  </h2>
                  <p className="mt-5 max-w-lg font-display text-lg text-charcoal" data-id="element-533" data-mp="hrmvzsuz5dds">
                    Portal will be notified and will prepare your final deliverables.
                  </p>
                  <div className="mt-8 flex flex-wrap items-center gap-6" data-id="element-534" data-mp="2c2s7tkg7sw47">
                    <button type="button" onClick={confirmApproval} className="label border border-ink bg-ink px-7 py-4 text-paper transition-colors duration-150 ease-out hover:bg-charcoal" data-id="element-535" data-mp="h16u6dy2ez2">
                      Confirm Approval
                    </button>
                    <button type="button" onClick={() => setPanel('idle')} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-536" data-mp="5dht0v1l2j9i">
                      Cancel
                    </button>
                  </div>
                </motion.div> : canApprove || canAddChanges ? <motion.div key="actions" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.2,
              ease: EASE
            }} data-id="element-537" data-mp="2uwa4jp2d1umm">
                  <div className="flex flex-wrap gap-5" data-id="element-538" data-mp="20bvg9kl0nzju">
                    {canApprove && !projectComplete && !settled && <button type="button" onClick={() => setPanel('confirm')} className="label flex-1 border border-ink bg-ink px-8 py-5 text-paper transition-colors duration-150 ease-out hover:bg-charcoal sm:flex-none" data-id="element-539" data-mp="1vaz12av7sqz4">
                        Approve
                      </button>}
                    {canAddChanges && <button type="button" onClick={() => setComposerOpen(true)} className="label flex-1 border border-ink px-8 py-5 text-ink transition-colors duration-150 ease-out hover:bg-ink hover:text-paper sm:flex-none" data-id="element-540" data-mp="2a9t1dz7kxg88">
                        {currentChanges.length > 0 ? 'Add Another Change' : 'Request Changes'}
                      </button>}
                  </div>

                  {changesRequested && <p className="label mt-6 text-mute" data-id="element-541" data-mp="3aao5lw5hw0w2">
                      Changes requested · Portal has been notified
                    </p>}
                </motion.div> : inReview ? <motion.p key="viewonly" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.2,
              ease: EASE
            }} className="label m-0 border border-rule px-7 py-8 text-mute" data-id="element-542" data-mp="3jbmbkjzhuiha">
                  Your access is view only · approvals sit with your team&rsquo;s approver
                </motion.p> : <motion.p key="editing" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.2,
              ease: EASE
            }} className="label m-0 border border-rule px-7 py-8 text-mute" data-id="element-543" data-mp="auri1v4l6afv">
                  This is with Portal · nothing to review yet
                </motion.p>}
            </AnimatePresence>
          </div>

          {composerOpen && canAddChanges && <form onSubmit={addChange} className="mt-8 border border-ink px-7 py-9 sm:px-10" data-id="element-544" data-mp="2gw2alq5ohstp">
              <h2 className="font-display text-2xl leading-tight sm:text-3xl" data-id="element-545" data-mp="2vkoqpu34qolz">
                What would you like us to change?
              </h2>
              <div className="mt-8 flex flex-col gap-8 sm:flex-row sm:items-start" data-id="element-546" data-mp="2qktx3iryzkx8">
                <label className="sm:w-32" data-id="element-547" data-mp="34kbpe84h5gbc">
                  <span className="label block text-graphite" data-id="element-548" data-mp="34jab7us5wdpe">Timecode</span>
                  <input value={timecode} onChange={event => setTimecode(event.target.value)} placeholder="e.g. 00:17" inputMode="numeric" className="mt-4 w-full border-0 border-b border-rule bg-transparent pb-3 font-display text-xl text-ink placeholder:text-mute focus:border-ink focus:outline-none" data-id="element-549" data-mp="2zmteic6wcr0p" />
                </label>
                <label className="flex-1" data-id="element-550" data-mp="383k91kkflhe1">
                  <span className="label block text-graphite" data-id="element-551" data-mp="382iv98c16gsm">Change Request</span>
                  <textarea value={changeNote} onChange={event => setChangeNote(event.target.value)} rows={3} placeholder="Describe the change required…" className="mt-4 w-full resize-none border-0 border-b border-rule bg-transparent pb-3 font-display text-xl text-ink placeholder:text-mute focus:border-ink focus:outline-none" data-id="element-552" data-mp="3c3ufszg1sbty" />
                </label>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-6" data-id="element-553" data-mp="nctlvau3jn7b">
                <button type="submit" className="label border border-ink px-7 py-4 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-554" data-mp="reonkgfxw2lm">
                  Add Change
                </button>
                <button type="button" onClick={() => setComposerOpen(false)} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-555" data-mp="3tv3hnhra962n">
                  Done
                </button>
              </div>
            </form>}

          {currentChanges.length > 0 && <section aria-labelledby="requested-changes" className="mt-16" data-id="element-556" data-mp="3b2styv579ep4">
              <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-rule pb-4" data-id="element-557" data-mp="3647wh3hc194d">
                <h2 id="requested-changes" className="label m-0 text-graphite" data-id="element-558" data-mp="316fu1fj2a4lx">
                  Requested Changes{item.version ? ` · ${item.version}` : ''}
                </h2>
                <p className="label-sm m-0 text-mute" data-id="element-559" data-mp="2s588ly1n8y99">
                  {currentChanges.length} change{currentChanges.length === 1 ? '' : 's'}
                </p>
              </div>
              <ul className="m-0 list-none p-0" data-id="element-560" data-mp="1boiw3923aq9b">
                {currentChanges.map(request => <li key={request.id} className="border-b border-rule py-6" data-id="element-561" data-mp="1gnm14ti2kwha">
                    <div className="flex flex-wrap items-baseline gap-3" data-id="element-562" data-mp="1glj0zgn7dmyu">
                      <span className="label text-ink" data-id="element-563" data-mp="1lm6rm17is1sj">
                        {request.timecode ? `${request.timecode} · ` : ''}
                        {request.authorName}
                      </span>
                      {request.resolved && <span className="label-sm text-mute" data-id="element-564" data-mp="svykd4eiv1sq">Completed</span>}
                    </div>
                    <p className="mt-2 max-w-2xl font-display text-xl leading-snug text-charcoal" data-id="element-565" data-mp="xv1g1y8m3vo1">
                      {request.body}
                    </p>
                    <p className="label-sm mt-3 text-mute" data-id="element-566" data-mp="57nnsdfuzoa9">Sent {request.time}</p>
                  </li>)}
              </ul>
            </section>}

          {item.versions.some(entry => entry.visibleToClient) && <section aria-labelledby="previous-versions" className="mt-16" data-id="element-567" data-mp="24hk7afpnl9h">
              <h2 id="previous-versions" className="label border-b border-rule pb-4 text-graphite" data-id="element-568" data-mp="3uaed1bf18qt2">
                Previous Versions
              </h2>
              <ul className="m-0 list-none p-0" data-id="element-569" data-mp="6yc9quhzyugh">
                {item.versions.filter(entry => entry.visibleToClient).map(entry => <li key={entry.id} className="flex flex-wrap items-baseline justify-between gap-4 border-b border-rule py-5" data-id="element-570" data-mp="3bd3oakr11frv">
                      <span className="flex items-baseline gap-5" data-id="element-571" data-mp="3kbp5ockrior2">
                        <span className="font-display text-2xl leading-none" data-id="element-572" data-mp="14kt5vwezv20n">{entry.version}</span>
                        <span className="label-sm text-mute" data-id="element-573" data-mp="zmrb4e87he9c">
                          {entry.label} · {entry.date}
                          {entry.changeCount ? ` · ${entry.changeCount} change${entry.changeCount === 1 ? '' : 's'}` : ''}
                        </span>
                      </span>
                      <button type="button" onClick={() => notify(`Simulated · would play ${entry.version}`)} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-574" data-mp="28l1065tdk5uv">
                        Watch
                      </button>
                    </li>)}
              </ul>
            </section>}

          {showAudio && <section aria-labelledby="audio-review" className="mt-16" data-id="element-575" data-mp="2wfu77dhaf8gq">
              <h2 id="audio-review" className="label border-b border-rule pb-4 text-graphite" data-id="element-576" data-mp="3k66cshp28t45">
                Audio Review
              </h2>
              <AudioMaster label={`${item.name} — Audio ${item.version}`} driveUrl={item.audioUrl} data-id="element-577" data-mp="3oyzf6yx23pd2" />
            </section>}

        </div>

        <aside className="lg:col-span-4 lg:border-l lg:border-rule lg:pl-12" data-id="element-578" data-mp="1k2vpsk1jtu0u">
          <h2 className="label border-b border-rule pb-4 text-graphite" data-id="element-579" data-mp="1f6f2fs26bjot">How Review Works</h2>
          <ol className="m-0 list-none p-0" data-id="element-580" data-mp="1a725ufli2y91">
            {[{
            step: '01',
            title: 'Watch',
            body: 'The latest version plays here inside Portal.'
          }, {
            step: '02',
            title: 'Respond',
            body: 'Add as many timecoded changes as you need.'
          }, {
            step: '03',
            title: 'Approve',
            body: 'Confirm the version once you are happy.'
          }, {
            step: '04',
            title: 'Download',
            body: 'Final deliverables move to Files after approval.'
          }].map(step => <li key={step.step} className="border-b border-rule py-6" data-id="element-581" data-mp="idjjh9szo40w">
                <div className="flex items-baseline gap-4" data-id="element-582" data-mp="2u39a2mc97gbg">
                  <span className="label text-mute" data-id="element-583" data-mp="2u0n2impd50t8">{step.step}</span>
                  <span className="font-display text-xl leading-none" data-id="element-584" data-mp="2y4l557dwctr1">{step.title}</span>
                </div>
                <p className="mt-3 font-display text-base leading-snug text-graphite" data-id="element-585" data-mp="331timuo3o9ly">{step.body}</p>
              </li>)}
          </ol>
          <p className="label-sm mt-7 leading-relaxed text-mute" data-id="element-586" data-mp="3cth8ir50dilb">
            Review copies stream from Google Drive · finals are delivered in Files
          </p>
        </aside>
      </div>
    </div>;
}